/**
 * The toolbar as DATA. Every formatting / insert action the rich-text
 * toolbar can offer is one `ToolbarItem` here; `toolbar-presets.ts` picks
 * and orders them per surface, `EditorToolbar.tsx` renders the row with
 * overflow, and `ToolbarMoreMenu.tsx` renders whatever didn't fit as a
 * searchable list.
 *
 * Adding a button = adding an entry here (label key, icon, group,
 * `activeStates`, `run`, search keywords) and placing it in a preset. The
 * external toolbar's active-state subscription is DERIVED from the
 * presets' `activeStates`, so a new toggle can no longer ship with a
 * stale highlight.
 */

import type { Editor } from '@tiptap/react'
import {
  Bold,
  Code,
  Ellipsis,
  Italic,
  Paperclip,
  Smile,
  Strikethrough,
  Subscript,
  Superscript,
  Type,
  Underline,
} from 'lucide-react'
import type { ComponentType, MouseEvent } from 'react'

import { announceToWiki } from '@/features/wiki/lib/use-wiki-announce'
import { i18n } from '@/i18n'
import { toggleOrInsertCodeBlock } from '../chat-code-block-keymap'
import type { EditorSurface } from '../extensions'
import { toggleListByLine } from '../list-item-editing'

import { insertDefaultTable } from '../nodes/table/insert-table'
import {
  BlockquoteIcon,
  BulletListIcon,
  CodeBlockIcon,
  LinkIcon,
  OrderedListIcon,
  TableIcon,
  TaskListIcon,
} from './icons'

export type ToolbarGroup = 'text-style' | 'inline' | 'block' | 'insert' | 'media'

/** ⋯ menu group an item is listed under (the menu's taxonomy is coarser than the row's). */
export type ToolbarMenuGroup = 'text' | 'lists' | 'insert' | 'media'

/**
 * Icon component. Lucide icons take `size` / `strokeWidth` / `className`;
 * the legacy inline-SVG icons in `./icons.tsx` take nothing and ignore
 * whatever they're handed — both are assignable here.
 */
export type ToolbarIcon = ComponentType<{
  size?: number | string
  strokeWidth?: number | string
  className?: string
}>

export type ToolbarItemId =
  | 'text-style'
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strike'
  | 'subscript'
  | 'superscript'
  | 'inline-code'
  | 'bullet-list'
  | 'ordered-list'
  | 'task-list'
  | 'quote'
  | 'link'
  | 'code-block'
  | 'table'
  | 'attach'
  | 'emoji'

/** Label lives in a namespace + key pair so wiki insert rows can reuse `wiki.slashMenu.*`. */
export interface ToolbarItemLabel {
  ns: 'common' | 'wiki'
  key: string
}

/**
 * What an item's `run` may reach for. Popovers are owned by the toolbar
 * component (they portal to `body`), so items open them through the
 * context rather than rendering their own.
 */
export interface ToolbarRunContext {
  surface: EditorSurface
  openLinkPopover: (event: MouseEvent<HTMLButtonElement>) => void
  openEmojiPicker: (event: MouseEvent<HTMLButtonElement>) => void
  /** Present only when the hosting surface can persist files. */
  attachFiles?: (files: File[]) => void
}

export interface ToolbarItem {
  id: ToolbarItemId
  label: ToolbarItemLabel
  /** Lucide component OR one of the legacy inline-SVG icon components. */
  icon: ToolbarIcon
  /** Row group — drives dividers between adjacent visible items. */
  group: ToolbarGroup
  /** ⋯ menu group. */
  menuGroup: ToolbarMenuGroup
  /**
   * Node / mark names `editor.isActive(name)` is polled for. Feeds the
   * external toolbar's re-render signature — under-listing leaves that
   * button's highlight stale, so list every name the item toggles.
   */
  activeStates: readonly string[]
  isActive?: (editor: Editor) => boolean
  run: (editor: Editor, ctx: ToolbarRunContext, event: MouseEvent<HTMLButtonElement>) => void
  /** Search aliases per locale — same shape as the wiki slash registry. */
  keywords: { en: readonly string[]; es: readonly string[] }
  /** TipTap keymap default, verified in `extensions.ts`. Display-only. */
  shortcut?: string
  /**
   * `'button'` (default) renders a `ToolbarButton`. `'custom'` items own
   * their trigger (the Tt dropdown, the emoji trigger) — the row still
   * measures them and the More menu still lists them.
   */
  render?: 'button' | 'custom'
  /** Glyph rendered INSIDE the button when it isn't an icon (B / I / S / X₂…). */
  glyph?:
    | 'bold'
    | 'italic'
    | 'underline'
    | 'strike'
    | 'inline-code'
    | 'subscript'
    | 'superscript'
    | 'emoji'
}

const item = (definition: ToolbarItem): ToolbarItem => definition

export const TOOLBAR_ITEMS: Readonly<Record<ToolbarItemId, ToolbarItem>> = {
  'text-style': item({
    id: 'text-style',
    label: { ns: 'common', key: 'editor.toolbar.normalText' },
    icon: Type,
    group: 'text-style',
    menuGroup: 'text',
    activeStates: ['heading', 'blockquote'],
    run: () => {},
    keywords: {
      en: ['heading', 'title', 'paragraph', 'text style', 'normal text', 'h1', 'h2', 'h3'],
      es: ['encabezado', 'título', 'párrafo', 'estilo de texto', 'texto normal'],
    },
    render: 'custom',
  }),
  bold: item({
    id: 'bold',
    label: { ns: 'common', key: 'editor.toolbar.bold' },
    icon: Bold,
    glyph: 'bold',
    group: 'inline',
    menuGroup: 'text',
    activeStates: ['bold'],
    isActive: (editor) => editor.isActive('bold'),
    run: (editor) => editor.chain().focus().toggleBold().run(),
    keywords: { en: ['bold', 'strong'], es: ['negrita'] },
    shortcut: 'Mod-B',
  }),
  italic: item({
    id: 'italic',
    label: { ns: 'common', key: 'editor.toolbar.italic' },
    icon: Italic,
    glyph: 'italic',
    group: 'inline',
    menuGroup: 'text',
    activeStates: ['italic'],
    isActive: (editor) => editor.isActive('italic'),
    run: (editor) => editor.chain().focus().toggleItalic().run(),
    keywords: { en: ['italic', 'emphasis'], es: ['cursiva', 'itálica'] },
    shortcut: 'Mod-I',
  }),
  underline: item({
    id: 'underline',
    label: { ns: 'common', key: 'editor.toolbar.underline' },
    icon: Underline,
    glyph: 'underline',
    group: 'inline',
    menuGroup: 'text',
    activeStates: ['underline'],
    isActive: (editor) => editor.isActive('underline'),
    run: (editor) => editor.chain().focus().toggleUnderline().run(),
    keywords: { en: ['underline', 'u'], es: ['subrayado', 'subrayar'] },
    shortcut: 'Mod-U',
  }),
  strike: item({
    id: 'strike',
    label: { ns: 'common', key: 'editor.toolbar.strikethrough' },
    icon: Strikethrough,
    glyph: 'strike',
    group: 'inline',
    menuGroup: 'text',
    activeStates: ['strike'],
    isActive: (editor) => editor.isActive('strike'),
    run: (editor) => editor.chain().focus().toggleStrike().run(),
    keywords: { en: ['strikethrough', 'strike', 'cross out'], es: ['tachado'] },
    shortcut: 'Mod-Shift-S',
  }),
  subscript: item({
    id: 'subscript',
    label: { ns: 'common', key: 'editor.toolbar.subscript' },
    icon: Subscript,
    glyph: 'subscript',
    group: 'inline',
    menuGroup: 'text',
    activeStates: ['subscript'],
    isActive: (editor) => editor.isActive('subscript'),
    run: (editor) => editor.chain().focus().toggleSubscript().run(),
    keywords: { en: ['subscript', 'sub'], es: ['subíndice'] },
    shortcut: 'Mod-,',
  }),
  superscript: item({
    id: 'superscript',
    label: { ns: 'common', key: 'editor.toolbar.superscript' },
    icon: Superscript,
    glyph: 'superscript',
    group: 'inline',
    menuGroup: 'text',
    activeStates: ['superscript'],
    isActive: (editor) => editor.isActive('superscript'),
    run: (editor) => editor.chain().focus().toggleSuperscript().run(),
    keywords: { en: ['superscript', 'sup', 'exponent'], es: ['superíndice'] },
    shortcut: 'Mod-.',
  }),
  'inline-code': item({
    id: 'inline-code',
    label: { ns: 'common', key: 'editor.toolbar.inlineCode' },
    icon: Code,
    glyph: 'inline-code',
    group: 'inline',
    menuGroup: 'text',
    activeStates: ['code'],
    isActive: (editor) => editor.isActive('code'),
    run: (editor) => editor.chain().focus().toggleCode().run(),
    keywords: { en: ['code', 'inline code', 'monospace'], es: ['código', 'código en línea'] },
    shortcut: 'Mod-E',
  }),
  'bullet-list': item({
    id: 'bullet-list',
    label: { ns: 'common', key: 'editor.toolbar.bulletList' },
    icon: BulletListIcon,
    group: 'block',
    menuGroup: 'lists',
    activeStates: ['bulletList'],
    isActive: (editor) => editor.isActive('bulletList'),
    run: (editor) => toggleListByLine(editor, 'bulletList'),
    keywords: {
      en: ['bullet list', 'bullets', 'unordered list', 'list'],
      es: ['lista', 'viñetas'],
    },
    shortcut: 'Mod-Shift-8',
  }),
  'ordered-list': item({
    id: 'ordered-list',
    label: { ns: 'common', key: 'editor.toolbar.orderedList' },
    icon: OrderedListIcon,
    group: 'block',
    menuGroup: 'lists',
    activeStates: ['orderedList'],
    isActive: (editor) => editor.isActive('orderedList'),
    run: (editor) => toggleListByLine(editor, 'orderedList'),
    keywords: {
      en: ['ordered list', 'numbered list', 'numbers', 'list'],
      es: ['lista numerada', 'lista ordenada', 'números'],
    },
    shortcut: 'Mod-Shift-7',
  }),
  'task-list': item({
    id: 'task-list',
    label: { ns: 'common', key: 'editor.toolbar.taskList' },
    icon: TaskListIcon,
    group: 'block',
    menuGroup: 'lists',
    activeStates: ['taskList'],
    isActive: (editor) => editor.isActive('taskList'),
    run: (editor) => editor.chain().focus().toggleTaskList().run(),
    keywords: {
      en: ['task list', 'checklist', 'checkbox', 'action item', 'todo'],
      es: ['lista de tareas', 'casilla', 'tarea'],
    },
    shortcut: 'Mod-Shift-9',
  }),
  quote: item({
    id: 'quote',
    label: { ns: 'common', key: 'editor.toolbar.quote' },
    icon: BlockquoteIcon,
    group: 'block',
    menuGroup: 'insert',
    activeStates: ['blockquote'],
    isActive: (editor) => editor.isActive('blockquote'),
    run: (editor) => editor.chain().focus().toggleBlockquote().run(),
    keywords: { en: ['quote', 'blockquote', 'citation'], es: ['cita', 'bloque de cita'] },
    shortcut: 'Mod-Shift-B',
  }),
  link: item({
    id: 'link',
    label: { ns: 'common', key: 'editor.toolbar.link' },
    icon: LinkIcon,
    group: 'insert',
    menuGroup: 'insert',
    activeStates: ['link'],
    isActive: (editor) => editor.isActive('link'),
    run: (_editor, ctx, event) => ctx.openLinkPopover(event),
    keywords: { en: ['link', 'url', 'hyperlink'], es: ['enlace', 'vínculo', 'url'] },
  }),
  'code-block': item({
    id: 'code-block',
    label: { ns: 'common', key: 'editor.toolbar.codeBlock' },
    icon: CodeBlockIcon,
    group: 'insert',
    menuGroup: 'insert',
    activeStates: ['codeBlock'],
    isActive: (editor) => editor.isActive('codeBlock'),
    // Chat: a caret in non-empty text inserts a NEW empty block below
    // instead of converting — a Shift+Enter multi-line paragraph is one
    // node and converting it swallowed every soft-wrapped line.
    run: (editor, ctx) =>
      ctx.surface === 'chat'
        ? toggleOrInsertCodeBlock(editor)
        : editor.chain().focus().toggleCodeBlock().run(),
    keywords: {
      en: ['code block', 'code', 'snippet', 'preformatted'],
      es: ['bloque de código', 'código', 'fragmento'],
    },
    shortcut: 'Mod-Alt-C',
  }),
  table: item({
    id: 'table',
    label: { ns: 'common', key: 'editor.toolbar.table' },
    icon: TableIcon,
    group: 'insert',
    menuGroup: 'insert',
    activeStates: [],
    run: (editor) => {
      // Announce the refusal rather than leaving the click unexplained. The
      // helper writes into the wiki page's live region and no-ops where there
      // isn't one, so the card surface is unaffected — the slash menu, which
      // is the other way into this on the wiki, announces the same string.
      if (!insertDefaultTable(editor)) {
        announceToWiki(i18n.t('wiki:slashMenu.blockedInsideTable'))
      }
    },
    keywords: { en: ['table', 'grid', 'rows', 'columns'], es: ['tabla', 'cuadrícula'] },
  }),
  attach: item({
    id: 'attach',
    label: { ns: 'common', key: 'editor.toolbar.attachFile' },
    icon: Paperclip,
    group: 'media',
    menuGroup: 'media',
    activeStates: [],
    run: (_editor, ctx) => {
      if (!ctx.attachFiles) return
      const input = document.createElement('input')
      input.type = 'file'
      input.multiple = true
      input.onchange = () => {
        const files = input.files ? Array.from(input.files) : []
        if (files.length > 0) ctx.attachFiles?.(files)
      }
      input.click()
    },
    keywords: {
      en: ['attach', 'attachment', 'upload', 'file', 'image', 'photo', 'video', 'pdf'],
      es: ['adjuntar', 'adjunto', 'subir', 'archivo', 'imagen', 'foto', 'video'],
    },
  }),
  emoji: item({
    id: 'emoji',
    label: { ns: 'common', key: 'editor.toolbar.emoji' },
    icon: Smile,
    glyph: 'emoji',
    group: 'media',
    menuGroup: 'media',
    activeStates: [],
    run: (_editor, ctx, event) => ctx.openEmojiPicker(event),
    keywords: { en: ['emoji', 'emoticon', 'smiley', 'reaction'], es: ['emoji', 'emoticono'] },
  }),
}

/** Reserved for the ⋯ trigger so `ToolbarMoreMenu` and presets share the glyph. */
export const MORE_ICON = Ellipsis
